import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Button } from '@mui/material';
import { Buffer } from "buffer";

import { IGeneratedDocument } from '../../types/document';

import { AppConfig } from '../../config/appConfig';
import { ShipmentService } from '../../services/shipmentService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Spinner from '../spinner';
import WorkingModal, { IWorkingModalProps } from '../workingModal';

import "../../styles/entity/waitingShipmentTable.scss";

interface IWaitingShipment {
	id?: number;
	bol?: string;
	customer?: string;
	vendor?: string;
	status?: string;
	reason?: string;
}

const WaitingShipmentTable = ({ }) => {
	const [data, setData] = useState<IWaitingShipment[]>([]);
	const [dataView, setDataView] = useState<IWaitingShipment[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.waitingShipmentTableDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoadingShipments, setIsLoadingShipments] = useState(true);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	useEffect(() => {
		setIsLoadingShipments(true);

		//GET DATA
		ShipmentService.getWaitingShipments()
			.then((response) => {
				console.log('ShipmentService.getWaitingShipments: SUCCESS', response);
				//Map the data you need
				let shipments: IWaitingShipment[] = [];
				if (response.data && response.data.length > 0) {
					response.data.map((item: any) => {
						shipments.push({
							id: item.id,
							bol: item.hwiNumber,
							customer: item.customerName,
							reason: item.waitingFor,
							status: item.status,
							vendor: item.vendorName
						});
					})
				}

				setData(shipments);
			})
			.catch((error) => {
				console.log('ShipmentService.getWaitingShipments: ERROR', error);
			})
			.finally(() => {
				setIsLoadingShipments(false);
			});
	}, [])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	useEffect(() => {
		if (data) {
			let newData = data.slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}
			if (sortField) {
				switch (sortField) {
					case "customer": {
						newData.sort((a, b) => UtilityService.compareStrings(a.customer, b.customer));
						break;
					}
					case "vendor": {
						newData.sort((a, b) => UtilityService.compareStrings(a.vendor, b.vendor));
						break;
					}
					case "status": {
						newData.sort((a, b) => UtilityService.compareStrings(a.status, b.status));
						break;
					}
					case "reason": {
						newData.sort((a, b) => UtilityService.compareStrings(a.reason, b.reason));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.bol, b.bol));
						break;
					}
				}

				if (!sortAscending) newData.reverse();

				setDataView(newData);
			}
		}
	}, [data, sortField, sortAscending])

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	const handleExportToExcel = () => {
		setWorkingModalParams({ params: { content: "Exporting waiting shipment list..." } });
		ShipmentService.exportWaitingShipments()
			.then((response) => {
				console.log('ShipmentService.exportWaitingShipments: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.error('ShipmentService.exportWaitingShipments: ERROR', error);
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	};

	const downloadFromByteArray = (file: IGeneratedDocument): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.contents, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}

	return (
		<div className="hwi-entity-search-results-table hwi-waiting-shipments-table">
			<section className="management-page-header">
				<div className="title">Shipments Flagged as Waiting</div>
				<div className="actions">
					<Button variant="contained" color="primary" disabled={!dataView?.length || isLoadingShipments} onClick={() => handleExportToExcel()}>Export to Excel</Button>
				</div>
			</section>
			<div className="headers">
				<div className="col-bol">
					<p onClick={() => handleHeaderClick("bol")}>
						BOL#
						{" "}
						{(sortField == "bol" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "bol" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-customer">
					<p onClick={() => handleHeaderClick("customer")}>
						Customer
						{" "}
						{(sortField == "customer" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "customer" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-vendor">
					<p onClick={() => handleHeaderClick("vendor")}>
						Vendor
						{" "}
						{(sortField == "vendor" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "vendor" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-status">
					<p onClick={() => handleHeaderClick("status")}>
						Status
						{" "}
						{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-reason">
					<p onClick={() => handleHeaderClick("reason")}>
						Waiting On
						{" "}
						{(sortField == "reason" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "reason" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="col-controls">{""}</div>
			</div>
			<div className="data">
				<Spinner dependency={isLoadingShipments} text="Searching for waiting shipments...">
					{dataView?.length > 0 ?
						(
							<>
								{dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
									<div className="row" key={item.id}>
										<div className="col-bol"><p>{item.bol}</p></div>
										<div className="col-customer"><p>{item.customer}</p></div>
										<div className="col-vendor"><p>{item.vendor}</p></div>
										<div className="col-status"><p>{item.status}</p></div>
										<div className="col-reason"><p>{item.reason}</p></div>
										{/* <div className="status-col"><p>{item.status}</p></div> */}
										<div className="col-controls"><p>
											<NavLink to={"/shipment/" + item.id}>
												<Icons.EditItem fill="#272f50" height="14px"></Icons.EditItem>
												{" "}
												Open
											</NavLink>
										</p></div>
									</div>
								))}
							</>
						) : (<div className="no-results">There are currently no waiting shipments.</div>)}					
				</Spinner>
			</div>
			{dataView?.length > 0 && !isLoadingShipments &&
				<div className="pager">
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
					<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
				</div>
			}
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</div>
	)
}

export default WaitingShipmentTable;